<script setup>
// La ficha de un producto: las fotos, lo que hay que saber y los dos caminos
// para pedirlo.
//
// Antes, tocar una tarjeta abría WhatsApp de inmediato. Convertía rápido a
// quien ya sabía lo que quería y perdía a quien todavía estaba mirando: no
// había dónde mirar. Ahora la tarjeta abre esto, y los dos botones están acá
// abajo, después de las fotos.
//
// Es un <dialog> nativo, no un div con posición fija: el navegador se encarga
// del foco atrapado adentro, de cerrar con Escape y del fondo. Menos código y
// más accesible que cualquier imitación.
import { computed, nextTick, ref, watch } from 'vue'
import { whatsappLink, trackConversion } from '@/lib/whatsapp'
import { trackProductView, trackQuoteCtaClick } from '@/lib/analytics'
import { requestQuoteFor } from '@/lib/quote_intent'
import ProductCarousel from '@/components/ProductCarousel.vue'
import LightMeter from '@/components/LightMeter.vue'
import WhatsappIcon from '@/components/WhatsappIcon.vue'

const props = defineProps({
  // null cuando no hay ficha abierta.
  product: { type: Object, default: null },
  phone: { type: String, required: true }
})

const emit = defineEmits([ 'close' ])

const dialog = ref(null)
const details = ref(null)

// El mismo criterio que ya usaba la tarjeta para escribir el mensaje: un roller
// se nombra por su tela y necesita el sustantivo delante; una persiana ya trae
// el suyo.
const subject = computed(() => {
  if (!props.product) return ''

  const name = props.product.name.toLowerCase()

  return /^(cortina|persiana)/.test(name) ? `una ${name}` : `una cortina ${name}`
})

const whatsappHref = computed(() => whatsappLink(props.phone, subject.value))
const photos = computed(() => props.product?.photos ?? [])

// El párrafo de la ficha. Cae al resumen de la tarjeta mientras nadie haya
// escrito uno: es preferible una línea corta a un espacio en blanco.
const description = computed(() => props.product?.description || props.product?.summary)

watch(() => props.product, async (product) => {
  if (product) {
    dialog.value?.showModal()

    // La ficha abre por su principio. Sin esto, tocar una tarjeta mientras la
    // página venía haciendo scroll suave deja la columna a media altura: el
    // desplazamiento que iba a la página termina en el primer contenedor que
    // sí puede scrollear, que es este.
    await nextTick()
    if (details.value) details.value.scrollTop = 0
    // El fondo no se mueve mientras la ficha está abierta: showModal atrapa el
    // foco, pero la rueda del mouse sigue haciendo scroll a la página de atrás.
    document.body.style.overflow = 'hidden'
    trackProductView(product.id)
  } else {
    dialog.value?.close()
    document.body.style.overflow = ''
  }
})

// Cerrar con Escape o con el fondo lo hace el navegador, y avisa con este
// evento. El padre es el que manda: acá solo se le cuenta.
function onClose () {
  document.body.style.overflow = ''
  emit('close')
}

// Clic fuera de la tarjeta. El <dialog> ocupa toda la pantalla, así que "fuera"
// es el propio dialog: si el clic cayó en él y no en su contenido, se cierra.
function onBackdropClick (event) {
  if (event.target === dialog.value) dialog.value.close()
}

function goToForm () {
  trackQuoteCtaClick('product-dialog')
  dialog.value?.close()
  requestQuoteFor(props.product?.id)
}

function goToMeasuring () {
  dialog.value?.close()
  document.getElementById('medir')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<template>
  <dialog
    ref="dialog"
    class="sheet-dialog"
    :aria-label="product ? `Ficha de ${product.name}` : undefined"
    @close="onClose"
    @click="onBackdropClick"
  >
    <!-- Una sola tarjeta: la foto y el texto comparten el mismo café, sin
         bloques dentro de bloques. Lo que separa es una línea, no un cambio de
         fondo.

         La altura la manda el contenido hasta el tope de la pantalla, y el
         contenido está medido para caber: la ficha se lee sin scrollear. El
         overflow-y-auto de la columna es la red por si un resumen largo se
         pasa, no la manera de leerla. -->
    <div
      v-if="product"
      class="flex max-h-[92dvh] flex-col lg:grid lg:h-[80dvh] lg:max-h-none lg:grid-cols-[1.05fr_1fr]"
    >
      <button
        type="button"
        class="absolute end-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-night/60 text-linen backdrop-blur transition hover:bg-night"
        aria-label="Cerrar"
        @click="dialog.close()"
      >
        <svg viewBox="0 0 24 24" fill="none" class="h-4.5 w-4.5" aria-hidden="true">
          <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>

      <div class="h-[28dvh] shrink-0 p-3 sm:h-[38dvh] sm:p-4 lg:h-full lg:min-h-0 lg:p-5">
        <ProductCarousel :photos="photos" :light="product.light" :label="product.name" />
      </div>

      <!-- Cada bloque lleva shrink-0: en una columna flex con altura acotada,
           lo que no cabe se aplasta en vez de scrollear, y la línea de la luz
           quedaba de dos píxeles de alto en un teléfono. -->
      <div
        ref="details"
        class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 pb-5 pt-1 sm:gap-5 sm:px-7 sm:pb-7 lg:py-7 lg:pe-7 lg:ps-2"
      >
        <!-- pe-12 en escritorio: ahí arriba está el botón de cerrar, y una
             ficha con el nombre largo le pasaba por debajo. -->
        <!-- Lo primero es el nombre: es lo que se fue a buscar al tocar la
             tarjeta. La ficha técnica venía arriba y hacía leer una línea de
             especificación antes de saber qué se estaba mirando. -->
        <div class="shrink-0 lg:pe-12">
          <h2 class="display text-balance text-[clamp(1.6rem,6vw,2.4rem)] text-linen">
            {{ product.name }}
          </h2>
          <p v-if="description" class="mt-2 leading-relaxed text-linen/75">
            {{ description }}
          </p>
        </div>

        <div class="shrink-0 space-y-2 border-y border-linen/10 py-3 sm:space-y-3 sm:py-4">
          <LightMeter :value="product.light" :adjustable="!!product.adjustable" />

          <p v-if="product.bestFor" class="text-sm leading-relaxed">
            <span class="eyebrow text-linen/45">Ideal para</span>
            <span class="ms-2 text-linen/80">{{ product.bestFor }}</span>
          </p>
        </div>

        <p class="shrink-0 text-sm leading-relaxed text-linen/55">
          ¿No sabes cómo medir?
          <button type="button" class="text-glow underline underline-offset-4 hover:text-linen" @click="goToMeasuring">
            Te ayudamos
          </button>
        </p>

        <!-- Los dos canales, uno al lado del otro y del mismo tamaño. Cuál
             conviene depende de la hora y de las ganas de escribir, no de cuál
             queremos empujar. En fila y no apilados: dos pastillas de ancho
             completo pesaban más que la cortina que se está mirando.

             El relleno ámbar es el del formulario y el contorno el de WhatsApp,
             igual que en el hero y en cada CTA de la página: si el amarillo
             cambia de canal según la sección, deja de significar algo. -->
        <div class="mt-auto shrink-0 pt-1">
          <div class="mt-2 grid grid-cols-2 gap-2.5">
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 rounded-full bg-glow px-3 py-2.5 text-sm font-semibold text-night transition-colors duration-300 hover:bg-[#f4a62a]"
              @click="goToForm"
            >
              <svg viewBox="0 0 24 24" fill="none" class="h-[1.05em] w-[1.05em] shrink-0" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="2" />
                <path d="m4 7.5 8 5 8-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Cotizar
            </button>

            <a
              :href="whatsappHref"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center justify-center gap-2 rounded-full border border-linen/25 px-3 py-2.5 text-sm font-semibold text-linen/90 transition-colors duration-300 hover:bg-linen/10"
              @click="trackConversion('product-dialog', subject)"
            >
              <WhatsappIcon class="h-[1.05em] w-[1.05em] shrink-0" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  </dialog>
</template>

